import React, { useState, useEffect, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { App, Alert, Form, Button, Typography, Row, Col, Switch, Divider, Tooltip } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { format } from 'date-fns'
import { loadStripe } from '@stripe/stripe-js'
import { useStripe, useElements, CardElement, Elements } from '@stripe/react-stripe-js'
import { useManualQuery } from 'graphql-hooks'
import isNumber from 'lodash/isNumber'

import Api from '@vacationtracker/shared/services/api'
import { getCompanyBillingInfoForDowngrade, getWorkWeekList } from '../../../graphql/custom-queries'
import * as logger from '../../../services/logger'
import { BillingCancelResubscribeActionAndInfo } from '../../../components/billing-cancel-actions-and-info'
import { notificationStore } from '../../../context/notificationsContext/store'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectLocaleSlice } from '../../../store/locale-slice'
import { selectAuthCompanySlice, setAuthCompanyPlan, setAuthCompanySubscriptionStatus, setAuthCompanyDefaultCurrency } from '../../../store/auth-company-slice'
import { getDateFnsLocale } from '@vacationtracker/shared/i18n'
import {
  selectCompanySettingsBillingUpdatedActionEventSlice,
  setCompanySettingsBillingUpdatedActionEvent,
} from '../../../store/company-settings-billing-updated-action-event-slice'
import { track } from '../../../services/analytics/analytics'
import { getAnalyticsProperties } from '../../../util/analytics-helpers'
import { openChat, sendChatMessage } from '../../../util/set-crisp-session-info'
import { ProductService } from '@vacationtracker/shared/data/stripe/product-service'
import { calculateFreemiumRestrictions, IPlanDowngradeSourceData } from '../../../util/calculate-freemium-restrictions'

import IntlMessages from '../../../util/IntlMessages'
import { PageHeader } from '../../../design-system/PageHeader'
import CircularProgress from '../../../components/circular-progress'
import PricePlanCard from '../../../components/price-plan-card'
import TryCompleteRequestCta from '../../../components/try-complete-request'
import BillingForm from '../../../components/billing-form'
import BillingInvoice from '../../../components/billing-invoice'
import PlanDowngradeModal from '../../../components/plan-downgrade-modal'
import PaymentInformation from '../../../components/payment-information'
import CancelSubscriptionModal from '../../../components/cancel-subscription-modal'
import CancelTrialSubscriptionModal from '../../../components/cancel-trial-subscription-modal'
import BillingCouponCode from '../../../components/billing-coupon-code'
import SwitchPlanModal from '../../../components/switch-plan-modal'
import LegacyPlanAlert from '../../../components/legacy-plan-alert'
import DowngradeAlert from '../../../components/downgrade-alert'
import BillingResubscribeAlert from '../../../components/billing-resubscribe-alert'
import BillingInformation from '../../../components/billing-information'
import { UpdateSeats } from '../../../components/update-seats'

import { IBillingInfo, IGetCompanyBillingInfoDataForDowngrade } from '../../../types/company'
import { IPaymentInfoResponse, PaymentMethodTypeEnum } from '@vacationtracker/shared/types/payment-information'
import { BillingTypesEnum, SubscriptionPeriod, USER_PLAN_LIMIT } from '@vacationtracker/shared/types/billing'
import { SubscriptionPlan, SubscriptionPlanEnum, TrialPeriod } from '@vacationtracker/shared/types/company'
import { IPrice, PriceName } from '@vacationtracker/shared/data/stripe/prices'
import { IUpcomingInvoicePrices, StripePaymentStatus } from '@vacationtracker/shared/types/stripe'
import { ICompanyWorkWeek, IGetCompanyWorkWeeks } from '../../../types/locations'
import { calculateTotalPrice } from '../../../util/billing'
import { useCurrency } from '../../../hooks/use-currency'
import { getCurrencyForCountry } from '@vacationtracker/shared/common/currency'
import countries from '@vacationtracker/shared/data/countries'

const { Paragraph, Title } = Typography

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = process.env.REACT_APP_STRIPE_KEY ? loadStripe(process.env.REACT_APP_STRIPE_KEY) : Promise.reject('No Stripe Key')

const CheckoutForm: React.FC<{ showFreemiumPlan: boolean }> = ({ showFreemiumPlan }) => {
  const { notification, modal } = App.useApp()
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)
  const [form] = Form.useForm()
  const { formatMessage } = useIntl()
  const { locale } = useAppSelector(selectLocaleSlice)
  const { formatPrice } = useCurrency()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { companySettingsBillingUpdatedActionEvent } = useAppSelector(selectCompanySettingsBillingUpdatedActionEventSlice)

  const [isLoading, setIsLoading] = useState(true)
  const [submitLoader, setSubmitLoader] = useState(false)
  const [stripeCustomerId, setStripeCustomerId] = useState('')
  const [isExistPaymentInfo, setIsExistPaymentInfo] = useState(false)
  const [paymentInfo, setPaymentInfo] = useState<IPaymentInfoResponse>({
    card: '',
    expirationDate: '',
    stripePaymentMethodId: '',
    type: PaymentMethodTypeEnum.card,
  })
  const [isLoadingPaymentInfo, setIsLoadingPaymentInfo] = useState(true)
  const [showCardField, setShowCardField] = useState(false)
  const [customerPortalLink, setCustomerPortalLink] = useState<string | undefined>()
  const [openCancelSubscriptionModal, setOpenCancelSubscriptionModal] = useState(false)
  const [openCancelTrialSubscriptionModal, setOpenCancelTrialSubscriptionModal] = useState(false)
  const [isTrialCanceled, setIsTrialCanceled] = useState(Boolean(localStorage.getItem('isTrialCanceled')))
  const [billing, setBilling] = useState<IBillingInfo>({
    paymentProcessor: '',
    paymentProcessorId: '',
    billingName: '',
    billingEmails: [],
    address: {
      city: '',
      country: '',
      address: '',
    },
    stripeCustomerId: '',
    stripePaymentMethodId: '',
    stripeSubscriptionId: '',
    stripeSubscriptionItemId: '',
    stripePriceId: '',
    stripeCurrentPeriodEnd: '',
    stripePaymentStatus: '',
    subscriptionPeriod: 'monthly',
    subscriptionPlan: 'Core',
    nextSubscriptionPeriod: 'monthly',
    nextSubscriptionPlan: 'Core',
  })
  const [showPlans, setShowPlans] = useState<boolean>(false)
  const [currentPlan, setCurrentPlan] = useState<SubscriptionPlan>('Core')
  const [currentPeriod, setCurrentPeriod] = useState<SubscriptionPeriod>('monthly')
  const [newPlan, setNewPlan] = useState<SubscriptionPlan>('Core')
  const [newPeriod, setNewPeriod] = useState<SubscriptionPeriod>('monthly')
  const [isLegacyPlan, setIsLegacyPlan] = useState<boolean>(true)
  const [numberOfUsers, setNumberOfUsers] = useState<number>(0)
  // License-based billing is annual-only; existing monthly license-based customers can still move to annual
  const isPeriodLockedToAnnual = billing.billingType === BillingTypesEnum.seatBased && currentPeriod === 'annual'
  const [openSwitchPlanModal, setOpenSwitchPlanModal] = useState(false)
  const [correlationId, setCorrelationId] = useState('')

  // Entity selection modal state
  const [showEntitySelectionModal, setShowEntitySelectionModal] = useState(false)
  const [pendingPlan, setPendingPlan] = useState<SubscriptionPlan | null>(null)
  const [trialPeriod, setTrialPeriod] = useState<TrialPeriod>()
  const isTrialPeriod = trialPeriod === 'in_trial'

  const [showBilllingForm, setShowBillingForm] = useState(false)
  const [currencyChangeAlertInfo, setCurrencyChangeAlertInfo] = useState<{ currentCurrency: string; newCurrency: string } | null>(null)
  const [numberOfSeats, setNumberOfSeats] = useState<number | undefined>(undefined)
  const [isLoadingUpcomingInvoiceInfo, setIsLoadingUpcomingInvoiceInfo] = useState(false)
  const [upcomingInvoiceInfoMessage, setUpcomingInvoiceInfoMessage] = useState<React.ReactElement | string>('')
  const [prices, setPrices] = useState<IUpcomingInvoicePrices | null>(null)
  const [isWithin14DaysOfBillingCycle, setIsWithin14DaysOfBillingCycle] = useState(false)

  // Store source data for plan downgrade restrictions computation
  const [planDowngradeSourceData, setPlanDowngradeSourceData] = useState<IPlanDowngradeSourceData | null>(null)

  // Compute plan downgrade restrictions from source data (memoized for performance)
  const freemiumRestrictions = useMemo(() => calculateFreemiumRestrictions(planDowngradeSourceData), [planDowngradeSourceData])

  // Determine if Freemium downgrade should be disabled (not within 14 days of billing cycle)
  const disableFreemiumDowngrade = !isTrialPeriod && !isWithin14DaysOfBillingCycle && (currentPlan === 'Core' || currentPlan === 'Complete')

  // Check if prices are loading - needed to show loading state for plan cards
  const arePricesLoading = !prices

  const stripe = useStripe()
  const elements = useElements()
  const dispatch = useAppDispatch()

  const [getCompanyBillingInfoQuery] = useManualQuery<IGetCompanyBillingInfoDataForDowngrade>(getCompanyBillingInfoForDowngrade)
  const [getWorkWeekListQuery] = useManualQuery<IGetCompanyWorkWeeks>(getWorkWeekList)

  useEffect(() => {
    if (companySettingsBillingUpdatedActionEvent) {
      fetchBilling()
      fetchPaymentInformation()
      dispatch(setCompanySettingsBillingUpdatedActionEvent(null))
    }
  }, [companySettingsBillingUpdatedActionEvent])

  useEffect(() => {
    if (stripeCustomerId) {
      getCustomerPortalLink().then((link: string) => {
        setCustomerPortalLink(link)
      })
    }
  }, [stripeCustomerId])

  useEffect(() => {
    if (Array.isArray(actionNotifications) && correlationId && !actionNotifications.includes(correlationId)) {
      track('PLAN_SWITCH_SUCCESS', {
        ...getAnalyticsProperties({
          activeUsers: numberOfUsers,
          stripePaymentStatus: billing.stripePaymentStatus,
        }),
        source: 'billing_page',
      })
      fetchBilling()
      fetchPaymentInformation()
    }
  }, [actionNotifications])

  useEffect(() => {
    // Track on mount - note: activeUsers/stripePaymentStatus not yet available
    track('BILLING_PAGE_VIEWED', {
      ...getAnalyticsProperties(),
      entryPoint: document.referrer ? new URL(document.referrer).pathname : undefined,
    })
    fetchPaymentInformation()
    fetchBilling()
  }, [])

  const fetchPaymentInformation = async () => {
    try {
      const paymentInfo = await Api.get<IPaymentInfoResponse>('/stripe/payment-information')
      if (paymentInfo.type === PaymentMethodTypeEnum.link) {
        // if payment info return empty card its basically same as 404, just needed for discounts
        // TODO refactor payment-information api and this component
        setIsExistPaymentInfo(true)
        setShowCardField(false)
        setPrices(paymentInfo.prices ?? productService.getCurrentPricesPerPlan())
        localStorage.removeItem('isTrialCanceled')
        setIsTrialCanceled(false)
      } else if (!paymentInfo.card) {
        setPrices(productService.getCurrentPricesPerPlan())
        setShowCardField(true)
      } else {
        setIsExistPaymentInfo(true)
        setShowCardField(false)
        setPrices(paymentInfo.prices ?? productService.getCurrentPricesPerPlan())
        localStorage.removeItem('isTrialCanceled')
        setIsTrialCanceled(false)
      }
      setPaymentInfo(paymentInfo)
      setIsLoadingPaymentInfo(false)
    } catch (e) {
      // Payment info api returns 404 for companies without card, or trial etc.. so we set the prices to newest prices in that case
      setPrices(productService.getCurrentPricesPerPlan())
      setIsLoadingPaymentInfo(false)
      setShowCardField(true)
    }
  }

  const fetchUpcomingInvoiceInfo = async () => {
    setIsLoadingUpcomingInvoiceInfo(true)
    try {
      const info = await Api.post('/stripe/upcoming-invoice-information', {})
      if (info) {
        setUpcomingInvoiceInfoMessage(
          formatMessage(
            { id: 'app.nextInvoice' },
            {
              amount: formatPrice(info?.nextInvoiceAmount ?? 0),
              startDate: format(new Date(info?.nextInvoiceDate as string), 'PPP', { locale: getDateFnsLocale(locale.locale) }),
            }
          )
        )
      }
    } catch {
      // A trial without a payment method has no upcoming invoice, and the API answers 404
      setUpcomingInvoiceInfoMessage('')
    } finally {
      setIsLoadingUpcomingInvoiceInfo(false)
    }
  }

  const fetchBilling = async () => {
    let workWeekList: ICompanyWorkWeek[] = []
    try {
      // since getCompanyBillingInfoQuery is called on many places, we shouldn't overload it with work week list which can be heavy, and thats why separate query
      const workWeekListResponse = await getWorkWeekListQuery()
      workWeekList = workWeekListResponse.data?.getWorkWeekList || []
    } catch (e) {
      // even if this try fails for some reason, we don't want to block the billing page, so we just log the error and continue
      // if the user wants to downgrade without setting default work hours, he will get error message from backend anyway
      logger.error('error fetching work week list', e)
    }

    try {
      const response = await getCompanyBillingInfoQuery()
      if (!response.data || response.error) throw response.error

      fetchUpcomingInvoiceInfo()
      setNumberOfUsers(response.data.getCompany.numberOfActiveUsers || 1)
      setTrialPeriod(response.data.getCompany.trialPeriod as TrialPeriod)

      // Store source data for plan downgrade restrictions (will be computed via useMemo)
      setPlanDowngradeSourceData({
        plan: response.data.getCompany.plan,
        teams: response.data.getTeamList,
        locations: response.data.getLocationList,
        leaveTypes: response.data.getLeaveTypeList,
        workWeeks: workWeekList,
      })

      const billing = response.data.getCompany.billing

      if (billing) {
        dispatch(setAuthCompanyPlan(response.data.getCompany.billing.subscriptionPlan))
        dispatch(setAuthCompanySubscriptionStatus(response.data.getCompany.billing.subscriptionStatus))
        dispatch(setAuthCompanyDefaultCurrency(response.data.getCompany.billing.defaultCurrency))
        setBilling(billing)
        if (billing.billingType === BillingTypesEnum.seatBased) {
          isNumber(billing.seats) && setNumberOfSeats(billing.seats)
        }
        setCurrentPlan(billing.nextSubscriptionPlan || billing.subscriptionPlan || 'Core')
        setNewPlan(billing.nextSubscriptionPlan || billing.subscriptionPlan || 'Core')
        if (billing.subscriptionPlan === 'Freemium') {
          // Freemium is always annual on Stripe, but we display it as monthly on the frontend.
          // This prevents Core and Complete from showing annual prices, which would appear too high.
          setCurrentPeriod('monthly')
        } else {
          setCurrentPeriod(billing.nextSubscriptionPeriod || billing.subscriptionPeriod || 'monthly')
          setNewPeriod(billing.nextSubscriptionPeriod || billing.subscriptionPeriod || 'monthly')
        }
        setStripeCustomerId(billing.stripeCustomerId)

        // Calculate if we're within 14 days of billing cycle end
        if (billing.stripeCurrentPeriodEnd) {
          const currentPeriodEndDate = new Date(Number(billing.stripeCurrentPeriodEnd) * 1000)
          // We set the currentPeriodEnd to the start of the day because if hours are included,
          // a user might try to switch to freemium at 10h, while Stripe shows that their subscription expires today at 15h.
          // Since users don’t see the time in the tooltip - only the date - this could be confusing.
          currentPeriodEndDate.setHours(0, 0, 0, 0)
          const fourteenDaysBeforeEnd = new Date(currentPeriodEndDate)
          fourteenDaysBeforeEnd.setDate(fourteenDaysBeforeEnd.getDate() - 14)
          const now = new Date()
          setIsWithin14DaysOfBillingCycle(now >= fourteenDaysBeforeEnd)
        }

        setSubmitLoader(false)

        form.setFieldsValue({
          billingName: billing.billingName,
          billingEmails: billing.billingEmails,
          city: billing.address && billing.address.city,
          country: billing.address && billing.address.country,
          state: billing.address && billing.address.state,
          address: billing.address && billing.address.address,
          addressLine2: billing.address && billing.address.addressLine2,
          sendInvoice: billing.sendInvoice ? billing.sendInvoice : false,
          promoCode: '',
        })
      }

      setIsLoading(false)
      setShowBillingForm(false)
      setCurrencyChangeAlertInfo(null)
    } catch (err) {
      logger.error('error fetching billing', err)
      setSubmitLoader(false)
    }
  }

  const submitBilling = async (seats?: number, isResubscribe?: boolean) => {
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable form submission until Stripe.js has loaded.
      return
    }

    // Until an address is saved, the billing form is mounted with empty required address and card fields,
    // so seat changes must not validate it or create a payment method from it.
    const isSeatsUpdate = isNumber(seats) && !isResubscribe

    let response
    try {
      const values = isSeatsUpdate ? {} : await form.validateFields()

      // Submit loader
      setSubmitLoader(true)

      // Get payment method from Stripe Card field
      let stripePaymentMethodId: string | undefined

      if (showCardField && !isSeatsUpdate) {
        const cardElement = elements.getElement(CardElement)
        if (!cardElement) {
          return
        }

        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
        })

        if (error || !paymentMethod) {
          logger.error(error)
          throw error
        } else {
          logger.error('[PaymentMethod]', paymentMethod)
        }

        stripePaymentMethodId = paymentMethod.id
      } else if (paymentInfo.stripePaymentMethodId) {
        stripePaymentMethodId = paymentInfo.stripePaymentMethodId
      }

      // Get address from form
      // Only include address if all required fields have values to avoid validation errors
      let address
      if (values.country && values.city && values.address) {
        address = {
          city: values.city,
          country: values.country,
          state: values.state || '',
          address: values.address,
          ...(values.addressLine2 && { addressLine2: values.addressLine2 }),
        }
      } else if (billing.address && billing.address.city && billing.address.country && billing.address.address) {
        // Use existing billing address if it's complete
        address = billing.address
      }

      response = await Api.post('/core/event', {
        eventType: 'BILLING_UPDATED',
        eventGroup: 'BILLING',
        paymentProcessor: 'stripe',
        billingName: values.billingName ?? billing.billingName,
        billingEmails: values.billingEmails ?? billing.billingEmails,
        ...(address && { address }),
        keepLegacyPlan: isLegacyPlan && currentPlan === newPlan,
        activeUsers: numberOfUsers,
        subscriptionPlan: currentPlan,
        subscriptionPeriod: currentPeriod,
        stripeCouponId: values.promoCode,
        stripePaymentMethodId,
        billingType: billing.billingType || BillingTypesEnum.payAsYouGo,
        ...(isNumber(seats) && { seats }),
        ...(isResubscribe && { resubscribe: true }),
      })
      if (!isSeatsUpdate) {
        track('BILLING_PAGE_CARD_ADDED', {})
        track('CARD_ADDED_TOTAL', {})
        track('new_subscription', {
          plan_name: currentPlan,
          plan_type: currentPeriod,
        })
      }
      track('BILLING_INFO_UPDATED', {
        ...getAnalyticsProperties({
          activeUsers: numberOfUsers,
          stripePaymentStatus: billing.stripePaymentStatus,
        }),
      })
      setCorrelationId(response.correlationId)
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: getSubmitLoaderText(!!paymentInfo.subscriptionCancelAtPeriodEnd) }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      setActionNotifications([...actionNotifications, response.correlationId])
    } catch (error) {
      handleBillingUpdateError(error, response?.correlationId)
    }
  }

  const resubscribe = async () => {
    // Preserve seats for seat-based billing when resubscribing
    const seatsToPass = billing.billingType === BillingTypesEnum.seatBased && isNumber(numberOfSeats) ? numberOfSeats : undefined
    await submitBilling(seatsToPass, true)
  }

  const updateBillingPlan = async (plan?: SubscriptionPlan, period?: SubscriptionPeriod, resubscribe?: boolean) => {
    let response
    try {
      const values = form.getFieldsValue(true)

      // Submit loader
      setSubmitLoader(true)

      // Get address from form if available, otherwise use existing billing address
      // Only include address if all required fields have values to avoid validation errors
      let address
      if (values.country && values.city && values.address) {
        address = {
          city: values.city,
          country: values.country,
          state: values.state || '',
          address: values.address,
          ...(values.addressLine2 && { addressLine2: values.addressLine2 }),
        }
      } else if (billing.address && billing.address.city && billing.address.country && billing.address.address) {
        // Use existing billing address if it's complete
        address = billing.address
      }

      response = await Api.post('/core/event', {
        eventType: 'BILLING_UPDATED',
        eventGroup: 'BILLING',
        paymentProcessor: 'stripe',
        billingName: values.billingName ?? billing.billingName,
        billingEmails: values.billingEmails ?? billing.billingEmails,
        ...(address && { address }),
        ...(resubscribe && { resubscribe: true }),
        activeUsers: numberOfUsers,
        keepLegacyPlan: isLegacyPlan && currentPlan === newPlan && currentPeriod === newPeriod,
        subscriptionPlan: plan || newPlan,
        subscriptionPeriod: period || newPeriod,
        billingType: billing.billingType || BillingTypesEnum.payAsYouGo,
        ...(isNumber(numberOfSeats) && { seats: numberOfSeats }),
      })

      track('BILLING_INFO_UPDATED', {
        ...getAnalyticsProperties({
          activeUsers: numberOfUsers,
          stripePaymentStatus: billing.stripePaymentStatus,
        }),
      })
      setCorrelationId(response.correlationId as string)
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: getSubmitLoaderText(!!paymentInfo.subscriptionCancelAtPeriodEnd) }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      setActionNotifications([...actionNotifications, response.correlationId])

      const isDowngrade = currentPlan === SubscriptionPlanEnum.complete && (plan || newPlan) === SubscriptionPlanEnum.core
      track(isDowngrade ? 'SUBSCRIPTION_DOWNGRADE_BUTTON_CLICKED' : 'SUBSCRIPTION_UPGRADE_BUTTON_CLICKED', {
        Previous_Plan: currentPlan,
        New_Plan: plan || newPlan,
      })
      if (currentPlan !== newPlan || currentPeriod !== newPeriod) {
        track('plan_change', {
          plan_new_name: newPlan || currentPlan,
          plan_new_type: newPeriod || currentPeriod,
          plan_old_name: currentPlan,
          plan_old_type: currentPeriod,
        })
      }
    } catch (error) {
      handleBillingUpdateError(error, response?.correlationId)
    }
  }

  const getSubmitLoaderText = (resubscribing: boolean): string => {
    if (resubscribing) {
      return 'billing.resubscribingUpdateInProgress'
    } else {
      return 'billing.updateInProgress'
    }
  }
  const handleOnCancelSubscription = async (isInTrial: boolean, cancelReason: string, cancelReasonComment?: string, integrationWithHr?: string) => {
    let response
    try {
      setSubmitLoader(true)
      response = await Api.post('/core/event', {
        eventType: 'SUBSCRIPTION_CANCELED',
        eventGroup: 'BILLING',
        paymentProcessor: 'stripe',
        activeUsers: numberOfUsers,
        cancelReason,
        cancelReasonComment,
        integrationWithHr,
        isInTrial,
        currentPeriod,
        currentPlan,
      })
      setOpenCancelSubscriptionModal(false)
      setOpenCancelTrialSubscriptionModal(false)
      notification.open({
        key: response.correlationId,
        message: formatMessage({ id: 'billing.cancelUpdateInProgress' }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      setCorrelationId(response.correlationId)
      setActionNotifications([...actionNotifications, response.correlationId])
      setSubmitLoader(false)
    } catch (error) {
      handleBillingUpdateError(error, response?.correlationId)
    }
  }

  const handleBillingUpdateError = (error, correlationId?: string, title?: string): void => {
    const description = correlationId
      ? formatMessage({ id: 'notifications.error' }, { correlationId })
      : error.response?.data?.message
        ? error.response?.data?.message
        : error.message
          ? error.message
          : JSON.stringify(error)

    // Track if error is related to missing payment information
    const errorMsg = error.response?.data?.message || error.message || ''
    if (errorMsg.toLowerCase().includes('payment') || errorMsg.toLowerCase().includes('card') || errorMsg.toLowerCase().includes('billing')) {
      track('BILLING_INFO_MISSING_ERROR_SHOWN', {
        ...getAnalyticsProperties({
          activeUsers: numberOfUsers,
          stripePaymentStatus: billing.stripePaymentStatus,
        }),
        errorMessage: errorMsg,
      })
    }

    notification.error({
      message: title ? title : formatMessage({ id: 'billing.updatedFaild' }),
      description,
      duration: 0,
    })
    setSubmitLoader(false)
  }

  const handleRemoveCard = () => {
    modal.confirm({
      title: formatMessage({ id: 'billing.removeCard.confirmTitle' }),
      content: formatMessage({ id: 'billing.removeCard.confirmContent' }),
      okType: 'danger',
      okText: formatMessage({ id: 'billing.removeCard.button' }),
      onOk: async () => {
        try {
          await Api.post('/stripe/detach-payment-method', {})
          await fetchPaymentInformation()
          await fetchBilling()
        } catch (error) {
          logger.error('Failed to remove credit card', error)
          handleBillingUpdateError(error)
        }
      },
    })
  }

  const getCustomerPortalLink = async (): Promise<string> => {
    const portalSession = await Api.post('/stripe/create-customer-portal-session', {
      stripeCustomerId,
      redirectUrl: window.location.href,
    })

    return portalSession.url
  }

  const changePlanHandler = (plan: SubscriptionPlan | 'support') => {
    if (plan === 'support' || plan == 'Enterprise') {
      // eslint-disable-next-line max-len
      const text =
        plan === 'Enterprise'
          ? "Hi there! I'm interested in your Enterprise plan. Tell me more!"
          : 'Hi there! I am interested in switching from our Core Annual plan to the Complete Monthly plan. Tell me more!'
      openChat()
      sendChatMessage(text)
    } else {
      // Check if entity selection modal is needed for downgrade
      const isDowngradeToFreemium = plan === 'Freemium' && (currentPlan === 'Core' || currentPlan === 'Complete')
      const isDowngradeToCore = plan === 'Core' && currentPlan === 'Complete'

      const needsModal =
        isDowngradeToFreemium || // Always show modal for Freemium downgrades (for limitations info, timing, confirmation)
        isDowngradeToCore // Always show modal for Complete → Core (for timing, probation period, confirmation)

      if (needsModal) {
        setPendingPlan(plan)
        setShowEntitySelectionModal(true)
      } else {
        setNewPlan(plan)
        if (isTrialPeriod) {
          updateBillingPlan(plan)
        } else {
          setOpenSwitchPlanModal(!openSwitchPlanModal)
        }
      }
    }
  }

  const handleEntitySelectionSubmit = async (selections: unknown) => {
    // The modal already handles sending the deletion/disable events
    // PlanDowngradeModal now shows all necessary information (timing, probation warning, entity selection)
    // So we can proceed directly with the plan change without showing SwitchPlanModal
    if (pendingPlan) {
      setNewPlan(pendingPlan)
      // Directly update billing plan - no need for SwitchPlanModal since all info was shown in PlanDowngradeModal
      updateBillingPlan(pendingPlan)
    }
    setShowEntitySelectionModal(false)
    setPendingPlan(null)

    // Return a resolved promise
    return Promise.resolve()
  }

  const handleEntitySelectionCancel = () => {
    setShowEntitySelectionModal(false)
    setPendingPlan(null)
  }

  const changePeriod = (period) => {
    const newPeriod = period ? 'annual' : 'monthly'
    setNewPeriod(newPeriod)
  }

  const cancelPlanChange = () => {
    setNewPlan(currentPlan)
    setOpenSwitchPlanModal(false)
  }

  const switchPlan = () => {
    updateBillingPlan()
    setOpenSwitchPlanModal(false)
  }

  const handleCountryChange = (countryIso: string) => {
    const currentCurrency = (authCompany?.billing?.defaultCurrency || 'usd').toUpperCase()
    const newCurrency = getCurrencyForCountry(countryIso).toUpperCase()

    if (currentCurrency === newCurrency) {
      setCurrencyChangeAlertInfo(null)
      return
    }

    const countryName = countries.find((c) => c.iso === countryIso)?.name || countryIso

    if (isTrialPeriod) {
      modal.confirm({
        title: formatMessage({ id: 'billing.currencyChangeModal.title' }),
        content: formatMessage(
          { id: 'billing.currencyChangeModal.content' },
          {
            country: countryName,
            currentCurrency,
            newCurrency,
          }
        ),
        onOk: async () => {
          try {
            const currentFormValues = form.getFieldsValue()
            await Api.put('/stripe/currency', { currency: newCurrency.toLowerCase() })
            await fetchBilling()
            await fetchPaymentInformation()
            // Restore form values the user entered (fetchBilling overwrites form with DB values)
            form.setFieldsValue({ country: countryIso, city: currentFormValues.city })
          } catch (error) {
            logger.error('Failed to change currency', error)
            form.setFieldsValue({ country: billing.address?.country || '' })
          }
        },
        onCancel: () => {
          form.setFieldsValue({ country: billing.address?.country || '' })
        },
      })
    } else {
      setCurrencyChangeAlertInfo({ currentCurrency, newCurrency })
    }
  }

  const cancelChangePlan = () => {
    setNewPlan(billing.subscriptionPlan)
    setNewPeriod(billing.subscriptionPeriod)
    updateBillingPlan(billing.subscriptionPlan, billing.subscriptionPeriod, true)
  }

  const layout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
  }

  const env = process.env.REACT_APP_ENVIRONMENT === 'production' ? 'prod' : 'test'
  const productService = new ProductService(env)

  const getCardPrice = (targetPlan: SubscriptionPlanEnum, targetPeriod: SubscriptionPeriod): IPrice => {
    if (targetPlan === SubscriptionPlanEnum.freemium) {
      return productService.getPriceByName(PriceName.FREEMIUM_2025)
    }
    if (!prices) {
      throw new Error('Backend prices not loaded')
    }
    return prices[targetPlan][targetPeriod]
  }

  return (
    <div className="main-content form-background">
      <PageHeader title={<IntlMessages id="sidebar.billing" />} />

      <div className="main-content-body">
        <div className="billing-body form-layout">
          {isLoading || isLoadingPaymentInfo ? (
            <CircularProgress />
          ) : (
            <Form
              form={form}
              layout="horizontal"
              name="billingForm"
              requiredMark="optional"
              size="large"
              onFinish={() => {
                submitBilling()
              }}
              {...layout}
            >
              {paymentInfo.subscriptionCancelAtPeriodEnd && paymentInfo.subscriptionCurrentPeriodEnd && (
                <>
                  <Row
                    gutter={{
                      xs: 8,
                      sm: 16,
                      md: 24,
                      lg: 32,
                    }}
                  >
                    <Col span={24}>
                      <BillingResubscribeAlert
                        stripePaymentStatus={billing.stripePaymentStatus}
                        subscriptionCurrentPeriodEnd={paymentInfo.subscriptionCurrentPeriodEnd}
                        resubscribe={() => {
                          resubscribe()
                        }}
                      />
                    </Col>
                  </Row>
                  <Divider />
                </>
              )}
              <Row
                gutter={{
                  xs: 8,
                  sm: 16,
                  md: 24,
                  lg: 32,
                }}
              >
                <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                  <Title level={4} style={{ marginTop: 8 }}>
                    <IntlMessages id="billing.pricingPlansTitle" />
                  </Title>
                  <Paragraph type="secondary">
                    <IntlMessages id="billing.pricingPlansText" />
                  </Paragraph>
                </Col>
                <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                  <Form.Item required={true} label={<IntlMessages id="billing.SubscriptionPlan" />}>
                    <div className="switch-plan-wrapper">
                      <div className="wrapper-plan-vt">
                        <div>
                          {(() => {
                            if (newPlan === 'Freemium') {
                              return (
                                <Title level={4}>
                                  <IntlMessages id="app.freemium" />
                                </Title>
                              )
                            }
                            if (arePricesLoading) {
                              return (
                                <>
                                  <Title level={4}>{newPlan}</Title>
                                  <Paragraph>
                                    <LoadingOutlined /> <IntlMessages id={newPeriod === 'annual' ? 'app.billing.periodAnnual' : 'app.monthly'} />
                                  </Paragraph>
                                </>
                              )
                            }
                            const cardPrice = getCardPrice(newPlan as SubscriptionPlanEnum, newPeriod)
                            const users = billing?.billingType === BillingTypesEnum.seatBased && isNumber(numberOfSeats) ? numberOfSeats : numberOfUsers
                            const effectiveUsers = Math.max(users, USER_PLAN_LIMIT)
                            const total = calculateTotalPrice(cardPrice, effectiveUsers)
                            const displayTotal =
                              authCompany?.isLegacyPlanCustomer && newPlan === 'Core' && newPeriod === 'monthly' ? (effectiveUsers <= 50 ? 25 : effectiveUsers * 1) : total
                            return (
                              <>
                                <Title level={4}>{newPlan}</Title>
                                <Paragraph>
                                  {formatPrice(displayTotal)} <IntlMessages id={newPeriod === 'annual' ? 'app.billing.periodAnnual' : 'app.monthly'} />
                                  {newPeriod === 'annual' &&
                                    (() => {
                                      const tierInfo = ProductService.getAnnualTierInfo(cardPrice, effectiveUsers)
                                      if (!tierInfo) return null
                                      return (
                                        <>
                                          &nbsp;
                                          <IntlMessages
                                            id="app.billing.tierRange"
                                            values={{ tierMin: tierInfo.tierMin, tierMax: tierInfo.tierMax === 'inf' ? '∞' : tierInfo.tierMax }}
                                          />{' '}
                                        </>
                                      )
                                    })()}
                                </Paragraph>
                              </>
                            )
                          })()}
                        </div>
                        <Button
                          type="primary"
                          disabled={isLoading}
                          onClick={() => {
                            track('SHOW_PLANS_CLICKED', {
                              ...getAnalyticsProperties({
                                activeUsers: numberOfUsers,
                                stripePaymentStatus: billing.stripePaymentStatus,
                              }),
                              plan: newPlan,
                              source: 'billing_page',
                            })
                            setShowPlans(!showPlans)
                          }}
                          ghost
                        >
                          <IntlMessages id={showPlans ? 'subscription.hidePlansButton' : 'subscription.showPlansButton'} />
                        </Button>
                      </div>
                    </div>
                  </Form.Item>
                </Col>
              </Row>

              {isExistPaymentInfo && !paymentInfo.subscriptionCancelAtPeriodEnd && !isTrialPeriod && (
                <DowngradeAlert
                  currentPlan={billing.subscriptionPlan}
                  currentPeriod={billing.subscriptionPeriod}
                  nextPlan={billing.nextSubscriptionPlan}
                  nextPeriod={billing.nextSubscriptionPeriod}
                  endDatePeriod={format(new Date(Number(billing.stripeCurrentPeriodEnd) * 1000), 'MMMM do yyyy', { locale: getDateFnsLocale(locale.locale) })}
                  cancelChangePlan={cancelChangePlan}
                />
              )}

              {showPlans && (
                <>
                  {!isPeriodLockedToAnnual && (
                    <Row
                      justify={showFreemiumPlan ? 'center' : 'end'}
                      gutter={{
                        xs: 8,
                        sm: 16,
                        md: 24,
                        lg: 32,
                      }}
                      style={{ marginBottom: 20 }}
                    >
                      <Col xl={showFreemiumPlan ? 32 : 16} xs={showFreemiumPlan ? 32 : 24}>
                        <div className="plan-period-switch">
                          <IntlMessages id="app.monthly" />
                          <Switch style={{ backgroundColor: 'grey' }} checked={newPeriod === 'annual'} onChange={changePeriod} />
                          <IntlMessages id="app.billing.periodAnnual" />
                        </div>
                      </Col>
                    </Row>
                  )}

                  {arePricesLoading ? (
                    <Row justify="center" style={{ marginBottom: 10, padding: '40px 0' }}>
                      <Col>
                        <CircularProgress />
                      </Col>
                    </Row>
                  ) : (
                    <Row justify={showFreemiumPlan ? 'center' : 'end'} style={{ marginBottom: 10 }} gutter={{ lg: 16, xl: numberOfUsers > 300 ? 4 : 8 }}>
                      {showFreemiumPlan && (
                        <Col xxl={{ span: 8, offset: 0 }} xl={8} lg={12} md={12} sm={24} xs={24} style={{ display: 'flex' }}>
                          <PricePlanCard
                            plan={SubscriptionPlanEnum.freemium}
                            price={getCardPrice(SubscriptionPlanEnum.freemium, newPeriod)}
                            totalUsers={billing?.billingType === BillingTypesEnum.seatBased && isNumber(numberOfSeats) ? numberOfSeats : numberOfUsers}
                            currentPeriod={currentPeriod}
                            newPeriod={newPeriod}
                            currentPlan={newPlan}
                            showRevertingInfo={true}
                            isSignup={false}
                            onSelectPLan={changePlanHandler}
                            isTrialPeriod={isTrialPeriod}
                            forceDisable={billing.stripePaymentStatus === 'past_due' || disableFreemiumDowngrade}
                            billingType={billing.billingType}
                            isLegacyPlanCustomer={authCompany?.isLegacyPlanCustomer}
                            disableLimitTeam={freemiumRestrictions.disableTeamsLimit}
                            disableLimitLocation={freemiumRestrictions.disableLocationsLimit}
                            disableLimitSubdepartments={freemiumRestrictions.disableSubdepartmentsLimit}
                            disableMultiLevelApproval={freemiumRestrictions.disableMultiLevelApproval}
                            disableNotificationWatchers={freemiumRestrictions.disableNotificationWatchers}
                            disableLimitLeaveTypes={freemiumRestrictions.disableLeaveTypesLimit}
                            totalSubdeparments={freemiumRestrictions.totalSubdepartments}
                            disableLimitWorkHours={freemiumRestrictions.disableCustomWorkWeek}
                            disableTooltipMessage={
                              disableFreemiumDowngrade
                                ? formatMessage(
                                    {
                                      id: currentPeriod === 'annual' ? 'billing.freemiumDowngradeRestrictionAnnual' : 'billing.freemiumDowngradeRestriction',
                                    },
                                    {
                                      date: format(new Date(Number(billing.stripeCurrentPeriodEnd) * 1000 - 14 * 24 * 60 * 60 * 1000), 'MMMM do yyyy', {
                                        locale: getDateFnsLocale(locale.locale),
                                      }),
                                    }
                                  )
                                : undefined
                            }
                            subscriptionCancelAtPeriodEnd={paymentInfo.subscriptionCancelAtPeriodEnd}
                            analyticsOverrides={{
                              activeUsers: numberOfUsers,
                              stripePaymentStatus: billing.stripePaymentStatus,
                            }}
                          />
                        </Col>
                      )}
                      <Col xxl={{ span: 8, offset: 0 }} xl={8} lg={12} md={12} sm={24} xs={24} style={{ display: 'flex' }}>
                        <PricePlanCard
                          plan={SubscriptionPlanEnum.core}
                          price={getCardPrice(SubscriptionPlanEnum.core, newPeriod)}
                          totalUsers={billing?.billingType === BillingTypesEnum.seatBased && isNumber(numberOfSeats) ? numberOfSeats : numberOfUsers}
                          currentPeriod={currentPeriod}
                          newPeriod={newPeriod}
                          currentPlan={newPlan}
                          showRevertingInfo={true}
                          isSignup={false}
                          onSelectPLan={changePlanHandler}
                          isTrialPeriod={isTrialPeriod}
                          forceDisable={billing.stripePaymentStatus === 'past_due'}
                          billingType={billing.billingType}
                          isLegacyPlanCustomer={authCompany?.isLegacyPlanCustomer}
                          disableLimitTeam={freemiumRestrictions.disableTeamsLimit}
                          disableLimitLocation={freemiumRestrictions.disableLocationsLimit}
                          disableLimitSubdepartments={freemiumRestrictions.disableSubdepartmentsLimit}
                          disableMultiLevelApproval={freemiumRestrictions.disableMultiLevelApproval}
                          disableNotificationWatchers={freemiumRestrictions.disableNotificationWatchers}
                          totalSubdeparments={freemiumRestrictions.totalSubdepartments}
                          disableLimitWorkHours={freemiumRestrictions.disableCustomWorkWeek}
                          subscriptionCancelAtPeriodEnd={paymentInfo.subscriptionCancelAtPeriodEnd}
                          analyticsOverrides={{
                            activeUsers: numberOfUsers,
                            stripePaymentStatus: billing.stripePaymentStatus,
                          }}
                        />
                      </Col>
                      <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24} style={{ display: 'flex' }}>
                        <PricePlanCard
                          plan={SubscriptionPlanEnum.complete}
                          price={getCardPrice(SubscriptionPlanEnum.complete, newPeriod)}
                          totalUsers={billing?.billingType === BillingTypesEnum.seatBased && isNumber(numberOfSeats) ? numberOfSeats : numberOfUsers}
                          currentPeriod={currentPeriod}
                          newPeriod={newPeriod}
                          currentPlan={newPlan}
                          showRevertingInfo={true}
                          isSignup={false}
                          onSelectPLan={changePlanHandler}
                          isTrialPeriod={isTrialPeriod}
                          forceDisable={billing.stripePaymentStatus === 'past_due'}
                          billingType={billing.billingType}
                          isLegacyPlanCustomer={authCompany?.isLegacyPlanCustomer}
                          subscriptionCancelAtPeriodEnd={paymentInfo.subscriptionCancelAtPeriodEnd}
                          analyticsOverrides={{
                            activeUsers: numberOfUsers,
                            stripePaymentStatus: billing.stripePaymentStatus,
                          }}
                          footerExtra={
                            <TryCompleteRequestCta
                              currentPlan={billing.subscriptionPlan}
                              currentPeriod={billing.subscriptionPeriod}
                              trialPeriod={trialPeriod}
                              activeUsers={numberOfUsers}
                              stripePaymentStatus={billing.stripePaymentStatus as StripePaymentStatus}
                              subscriptionCancelAtPeriodEnd={paymentInfo.subscriptionCancelAtPeriodEnd}
                              upcomingInvoiceInfo={upcomingInvoiceInfoMessage}
                            />
                          }
                        />
                      </Col>
                    </Row>
                  )}
                </>
              )}
              {authCompany?.isLegacyPlanCustomer && <LegacyPlanAlert />}

              <div style={{ marginTop: 30 }}>
                <Divider />

                {billing.billingType === BillingTypesEnum.seatBased && billing.seats && (
                  <>
                    <Row
                      gutter={{
                        xs: 8,
                        sm: 16,
                        md: 24,
                        lg: 32,
                      }}
                    >
                      <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                        <Title level={4} style={{ marginTop: 8 }}>
                          <IntlMessages id="billing.seats.manage" />
                        </Title>
                      </Col>
                      <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                        <UpdateSeats
                          showLabel
                          onSubmit={(seats: number) => {
                            submitBilling(seats)
                          }}
                          numberOfSeats={billing.seats}
                          numberOfActiveUsers={numberOfUsers}
                          locale={locale.locale}
                          inputLayout={{
                            xl: { span: 3 },
                            lg: { span: 3 },
                            md: { span: 3 },
                            sm: { span: 24 },
                            xs: { span: 24 },
                          }}
                          isAnnualBilling={billing.subscriptionPeriod === 'annual'}
                        />
                      </Col>
                    </Row>

                    <Divider />
                  </>
                )}

                <Row
                  gutter={{
                    xs: 8,
                    sm: 16,
                    md: 24,
                    lg: 32,
                  }}
                >
                  <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                    <Title level={4} style={{ marginTop: 8 }}>
                      <IntlMessages id="components.billingInfo.title" />
                    </Title>
                    <Paragraph type="secondary">
                      <IntlMessages id="components.billingInfo.text" />
                    </Paragraph>
                  </Col>

                  <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                    {showBilllingForm || !billing.address ? (
                      <BillingForm
                        form={form}
                        showPayment={showCardField}
                        disabled={paymentInfo.subscriptionCancelAtPeriodEnd}
                        currentPlan={currentPlan}
                        onCountryChange={handleCountryChange}
                      />
                    ) : (
                      <BillingInformation billingInfo={billing} />
                    )}

                    {currencyChangeAlertInfo && !isTrialPeriod && (showBilllingForm || !billing.address) && (
                      <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={
                          <IntlMessages
                            id="billing.currencyChangeAlert.activeSubscription"
                            values={{
                              currentCurrency: currencyChangeAlertInfo.currentCurrency,
                              newCurrency: currencyChangeAlertInfo.newCurrency,
                            }}
                          />
                        }
                      />
                    )}

                    {!paymentInfo.subscriptionCancelAtPeriodEnd && (showBilllingForm || !billing.address) && (
                      <Form.Item wrapperCol={{ span: 24 }} style={{ textAlign: 'right' }}>
                        <Button
                          className="button-right"
                          onClick={() => {
                            setShowBillingForm(!showBilllingForm)
                            setCurrencyChangeAlertInfo(null)
                          }}
                        >
                          <IntlMessages id="app.cancel" />
                        </Button>
                        <Button className="button-right" type="primary" loading={submitLoader} htmlType="submit" disabled={submitLoader}>
                          <IntlMessages id="billing.updateBillingInfo" />
                        </Button>
                      </Form.Item>
                    )}

                    {Boolean(billing.address) && !showBilllingForm && (
                      <Form.Item wrapperCol={{ span: 24 }} style={{ textAlign: 'right' }}>
                        <Button className="button-right" disabled={paymentInfo.subscriptionCancelAtPeriodEnd} onClick={() => setShowBillingForm(!showBilllingForm)}>
                          <IntlMessages id="app.edit" />
                        </Button>
                      </Form.Item>
                    )}
                  </Col>
                </Row>

                <Divider />

                <Row
                  gutter={{
                    xs: 8,
                    sm: 16,
                    md: 24,
                    lg: 32,
                  }}
                >
                  <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                    <Title level={4} style={{ marginTop: 8 }}>
                      <IntlMessages id="components.billingCouponCode.title" />
                    </Title>
                    <Paragraph type="secondary">
                      <IntlMessages id="components.billingCouponCode.text" />
                    </Paragraph>
                  </Col>
                  <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                    <BillingCouponCode discounts={paymentInfo.discounts || []} onCouponApplied={fetchPaymentInformation} showApplyButton={true} />
                  </Col>
                </Row>

                {!showCardField && (
                  <PaymentInformation
                    customerPortalLink={customerPortalLink}
                    card={paymentInfo?.card}
                    expirationDate={paymentInfo?.expirationDate}
                    type={paymentInfo.type}
                    showRemoveCard={currentPlan === SubscriptionPlanEnum.freemium && isExistPaymentInfo && paymentInfo.type === PaymentMethodTypeEnum.card}
                    onRemoveCard={handleRemoveCard}
                  />
                )}
                {isExistPaymentInfo && (
                  <BillingInvoice
                    customerPortalLink={customerPortalLink}
                    upcomingInvoiceInfoMessage={upcomingInvoiceInfoMessage}
                    isLoadingUpcomingInvoiceInfo={isLoadingUpcomingInvoiceInfo}
                  />
                )}
              </div>
            </Form>
          )}

          <BillingCancelResubscribeActionAndInfo
            stripeCurrentPeriodEnd={billing.stripeCurrentPeriodEnd}
            subscriptionCancelAtPeriodEnd={paymentInfo.subscriptionCancelAtPeriodEnd}
            hasPaymentInfo={isExistPaymentInfo}
            isTrialPeriod={isTrialPeriod}
            isTrialCanceled={isTrialCanceled}
            setOpenCancelTrialSubscriptionModal={setOpenCancelTrialSubscriptionModal}
            setOpenCancelSubscriptionModal={setOpenCancelSubscriptionModal}
            resubscribe={() => {
              resubscribe()
            }}
          />
        </div>
      </div>

      {openSwitchPlanModal && (
        <SwitchPlanModal
          openSwitchPlanModal={openSwitchPlanModal}
          currentPlan={currentPlan}
          cancelPlanChange={cancelPlanChange}
          switchPlan={switchPlan}
          newPlan={newPlan}
          newPeriod={newPeriod}
          isLegacyPlan={authCompany?.isLegacyPlanCustomer || false}
          currentPeriod={currentPeriod}
          endDatePeriod={format(new Date(Number(billing.stripeCurrentPeriodEnd) * 1000), 'MMMM do yyyy', { locale: getDateFnsLocale(locale.locale) })}
          submitLoader={submitLoader}
          analyticsOverrides={{
            activeUsers: numberOfUsers,
            stripePaymentStatus: billing.stripePaymentStatus,
          }}
        />
      )}

      {openCancelSubscriptionModal && (
        <CancelSubscriptionModal
          openCancelSubscriptionModal={openCancelSubscriptionModal}
          handleOnCancelSubscription={(cancelReason, cancelReasonComment, integrationWithHr) =>
            handleOnCancelSubscription(false, cancelReason, cancelReasonComment, integrationWithHr)
          }
          closeModal={() => setOpenCancelSubscriptionModal(false)}
        />
      )}

      {openCancelTrialSubscriptionModal && (
        <CancelTrialSubscriptionModal
          openCancelSubscriptionModal={openCancelTrialSubscriptionModal}
          handleOnCancelSubscription={(cancelReason, cancelReasonComment, integrationWithHr) =>
            handleOnCancelSubscription(true, cancelReason, cancelReasonComment, integrationWithHr)
          }
          closeModal={() => setOpenCancelTrialSubscriptionModal(false)}
          setOpenCancelTrialSubscriptionModal={setOpenCancelTrialSubscriptionModal}
          setIsTrialCanceled={setIsTrialCanceled}
          hasPaymentInfo={isExistPaymentInfo}
        />
      )}

      {/* Entity Selection Modal for Downgrade */}
      {planDowngradeSourceData && pendingPlan && (pendingPlan === 'Freemium' || pendingPlan === 'Core') && (
        <PlanDowngradeModal
          visible={showEntitySelectionModal}
          onCancel={handleEntitySelectionCancel}
          onSubmit={handleEntitySelectionSubmit}
          sourceData={{
            teams: planDowngradeSourceData.teams || [],
            locations: planDowngradeSourceData.locations || [],
            leaveTypes: planDowngradeSourceData.leaveTypes || [],
          }}
          downgradeType={pendingPlan === 'Freemium' ? 'to-freemium' : 'complete-to-core'}
          targetPlan={pendingPlan}
          currentPeriod={currentPeriod}
          endDatePeriod={format(new Date(Number(billing.stripeCurrentPeriodEnd) * 1000), 'MMMM do yyyy', { locale: getDateFnsLocale(locale.locale) })}
          numberOfActiveUsers={numberOfUsers}
          isTrialPeriod={isTrialPeriod}
          analyticsOverrides={{
            activeUsers: numberOfUsers,
            stripePaymentStatus: billing.stripePaymentStatus,
          }}
        />
      )}
    </div>
  )
}

const StripeBillingPage = ({ showFreemiumPlan }: { showFreemiumPlan: boolean }) => {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm showFreemiumPlan={showFreemiumPlan} />
    </Elements>
  )
}

export default StripeBillingPage
